import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { formatToTimeZone, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';

import { ActivityLogTaskCandidateRsvp_ActivityLogFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from '../../Helpers/ActivityLogBase';
import ActivityLogContent from '../../Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogTaskCandidateRsvp_ActivityLogFragment;
}

const ActivityLogTaskCandidateRsvp: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogRsvpDetails') {
    throw new Error('ActivityLogTaskCandidateRsvp: details is not ActivityLogRsvpDetails');
  }

  const { startAt, endAt, responseStatus, comment } = details;
  const actionLabel = useMemo(() => {
    const timezone = getLocalTimezone();

    const startDate = formatToTimeZone(startAt, 'MMM d, yyyy', timezone);
    const startTime = formatToTimeZone(startAt, 'h:mm a', timezone);
    const endTime = formatToTimeZone(endAt, 'h:mm a', timezone);

    const timezoneAbbr = getTZAbbr(timezone);

    switch (responseStatus) {
      case ResponseStatus.Accepted:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.acceptedInterviewOn',
          fallback: `accepted their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
          values: {
            startDate,
            startTime,
            endTime,
            timezone: timezoneAbbr,
          },
        };
      case ResponseStatus.Declined:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.declinedInterviewOn',
          fallback: `declined their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
          values: {
            startDate,
            startTime,
            endTime,
            timezone: timezoneAbbr,
          },
        };
      case ResponseStatus.Tentative:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.respondedMaybeForInterviewOn',
          fallback: `responded maybe for their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
          values: {
            startDate,
            startTime,
            endTime,
            timezone: timezoneAbbr,
          },
        };
      default:
        return undefined;
    }
  }, [endAt, responseStatus, startAt]);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      responseStatus={responseStatus}
      actionLabel={actionLabel?.fallback}
      actionLabelKey={actionLabel?.key}
      actionLabelValues={actionLabel?.values}
    >
      {comment ? <ActivityLogContent content={comment} /> : null}
    </ActivityLogBase>
  );
};

ActivityLogTaskCandidateRsvp.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogTaskCandidateRsvp_activityLog on ActivityLog {
      id
      details {
        ... on ActivityLogRsvpDetails {
          startAt
          endAt
          responseStatus
          comment
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskCandidateRsvp;
